import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Calendar, 
  Clock, 
  TrendingUp, 
  PieChart, 
  BarChart3,
  RefreshCw,
  Filter,
  ChevronDown,
  Tag,        // Nuevo
  Globe,      // Nuevo
  Target      // Nuevo
} from 'lucide-react';
import { analyticsService } from '../../firebase/analyticsService';
import { adminService } from '../../firebase/adminService';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const navigate = useNavigate();
  
  // Estados existentes
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [occupancyData, setOccupancyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  
  // NUEVO: Estado para métricas de marketing
  const [marketingMetrics, setMarketingMetrics] = useState(null);
  
  // Estados para filtros
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // NUEVO: Estado para vista de marketing
  const [marketingView, setMarketingView] = useState('overview'); // 'overview', 'promo', 'source', 'combo'
  
  // Calcular fechas según el período seleccionado (igual que antes)
  const getDateRange = () => {
    const today = new Date();
    let startDate, endDate;
    
    switch (selectedPeriod) {
      case 'day':
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case 'week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 6);
        endDate = new Date(today);
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today);
        break;
      case 'custom':
        startDate = customStartDate ? new Date(customStartDate) : new Date(today);
        endDate = customEndDate ? new Date(customEndDate) : new Date(today);
        break;
      default:
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 6);
        endDate = new Date(today);
    }
    
    return { startDate, endDate };
  };

  // Función actualizada para cargar todos los datos incluyendo marketing
  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { startDate, endDate } = getDateRange();
      
      // Cargar datos en paralelo (añadimos marketing)
      const [
        advancedStats,
        dailyChart,
        occupancy,
        marketing  // NUEVO
      ] = await Promise.all([
        analyticsService.calculateAdvancedStats(startDate, endDate),
        analyticsService.getDailyChartData(startDate, endDate),
        analyticsService.calculateOccupancyLevel(startDate, endDate),
        analyticsService.getMarketingMetrics(startDate, endDate)  // NUEVO
      ]);
      
      setStats(advancedStats);
      setChartData(dailyChart);
      setOccupancyData(occupancy);
      setMarketingMetrics(marketing);  // NUEVO
      setLastUpdateTime(new Date());
      
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Efecto para cargar datos cuando cambia el período
  useEffect(() => {
    fetchAllData();
  }, [selectedPeriod, customStartDate, customEndDate]);

  // Función para obtener el texto del período (igual que antes)
  const getPeriodText = () => {
    const { startDate, endDate } = getDateRange();
    const formatDate = (date) => date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
    
    switch (selectedPeriod) {
      case 'day':
        return `Hoy (${formatDate(startDate)})`;
      case 'week':
        return `Últimos 7 días (${formatDate(startDate)} - ${formatDate(endDate)})`;
      case 'month':
        return `Este mes (${formatDate(startDate)} - ${formatDate(endDate)})`;
      case 'custom':
        return `Período personalizado (${formatDate(startDate)} - ${formatDate(endDate)})`;
      default:
        return 'Período seleccionado';
    }
  };

  // Componente de loading
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          <p className="text-gray-600">Cargando métricas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header con filtros (igual que antes) */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Analytics</h1>
          <p className="text-gray-600 mt-1">{getPeriodText()}</p>
          {lastUpdateTime && (
            <p className="text-sm text-gray-500 mt-1">
              Última actualización: {lastUpdateTime.toLocaleTimeString('es-ES')}
            </p>
          )}
        </div>
        
        {/* Controles de filtros (igual que antes) */}
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <div className="relative">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 min-w-[200px] justify-between"
            >
              <div className="flex items-center gap-2">
                <Filter size={18} />
                <span className="text-sm">
                  {selectedPeriod === 'day' && 'Hoy'}
                  {selectedPeriod === 'week' && 'Últimos 7 días'}
                  {selectedPeriod === 'month' && 'Este mes'}
                  {selectedPeriod === 'custom' && 'Personalizado'}
                </span>
              </div>
              <ChevronDown size={16} className={`transform transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
            
            {/* Menú desplegable de filtros */}
            {showFilters && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 min-w-[250px]">
                <div className="p-3 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Período</label>
                    <div className="space-y-2">
                      {[
                        { value: 'day', label: 'Hoy' },
                        { value: 'week', label: 'Últimos 7 días' },
                        { value: 'month', label: 'Este mes' },
                        { value: 'custom', label: 'Personalizado' }
                      ].map(period => (
                        <label key={period.value} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="period"
                            value={period.value}
                            checked={selectedPeriod === period.value}
                            onChange={(e) => setSelectedPeriod(e.target.value)}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-sm">{period.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {selectedPeriod === 'custom' && (
                    <div className="space-y-2 pt-2 border-t">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Fecha inicio</label>
                        <input
                          type="date"
                          value={customStartDate}
                          onChange={(e) => setCustomStartDate(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Fecha fin</label>
                        <input
                          type="date"
                          value={customEndDate}
                          onChange={(e) => setCustomEndDate(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="pt-2 border-t">
                    <button
                      onClick={() => setShowFilters(false)}
                      className="w-full px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Aplicar
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={fetchAllData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Actualizar</span>
          </button>
        </div>
      </div>

      {/* Mostrar errores si los hay */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <div className="flex justify-between items-center">
            <span>{error}</span>
            <button 
              className="text-red-700 hover:text-red-900"
              onClick={() => setError(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* SECCIÓN NUEVA: Métricas de Marketing (Arriba) */}
      {marketingMetrics && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Target className="h-6 w-6 text-purple-600" />
              Métricas de Marketing
            </h2>
            
            {/* Selector de vista de marketing */}
            <div className="flex gap-2">
              {[
                { value: 'overview', label: 'General' },
                { value: 'promo', label: 'Promociones' },
                { value: 'source', label: 'Orígenes' },
                { value: 'combo', label: 'Combinado' }
              ].map(view => (
                <button
                  key={view.value}
                  onClick={() => setMarketingView(view.value)}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    marketingView === view.value 
                      ? 'bg-purple-600 text-white' 
                      : 'bg-white text-gray-700 hover:bg-purple-100'
                  }`}
                >
                  {view.label}
                </button>
              ))}
            </div>
          </div>

          {/* Vista General de Marketing */}
          {marketingView === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600">Con Promo</p>
                    <p className="text-2xl font-bold text-purple-600 mt-1">
                      {marketingMetrics.totals.withPromo}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {stats && Math.round((marketingMetrics.totals.withPromo / stats.overview.totalReservations) * 100)}%
                    </p>
                  </div>
                  <Tag className="h-5 w-5 text-purple-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600">Con Origen</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      {marketingMetrics.totals.withSource}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {stats && Math.round((marketingMetrics.totals.withSource / stats.overview.totalReservations) * 100)}%
                    </p>
                  </div>
                  <Globe className="h-5 w-5 text-green-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600">Tracking Total</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">
                      {marketingMetrics.totals.withBoth}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Completo
                    </p>
                  </div>
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600">Promos Activas</p>
                    <p className="text-2xl font-bold text-orange-600 mt-1">
                      {marketingMetrics.totals.uniquePromos}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      códigos
                    </p>
                  </div>
                  <BarChart3 className="h-5 w-5 text-orange-500" />
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600">Canales</p>
                    <p className="text-2xl font-bold text-pink-600 mt-1">
                      {marketingMetrics.totals.uniqueSources}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      orígenes
                    </p>
                  </div>
                  <PieChart className="h-5 w-5 text-pink-500" />
                </div>
              </div>
            </div>
          )}

          {/* Vista de Promociones */}
          {marketingView === 'promo' && Object.keys(marketingMetrics.byPromo).length > 0 && (
            <div className="bg-white rounded-lg p-4">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-purple-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Código
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Reservas
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Comensales
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Confirmadas
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Tasa Éxito
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {Object.entries(marketingMetrics.byPromo).map(([code, data]) => (
                      <tr key={code} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                            {code}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {data.count}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {data.guests}
                        </td>
                        <td className="px-4 py-3 text-sm text-green-600 font-medium">
                          {data.confirmed}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: `${data.count > 0 ? (data.confirmed / data.count) * 100 : 0}%` }}
                              ></div>
                            </div>
                            <span className="text-gray-900 font-medium">
                              {data.count > 0 ? ((data.confirmed / data.count) * 100).toFixed(0) : 0}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Mejor promoción */}
              {marketingMetrics.topPerformers.bestPromo && (
                <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-sm text-purple-900">
                    🏆 Mejor promoción: <strong>{marketingMetrics.topPerformers.bestPromo.code}</strong> con {marketingMetrics.topPerformers.bestPromo.data.count} reservas
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Vista de Orígenes */}
          {marketingView === 'source' && Object.keys(marketingMetrics.bySource).length > 0 && (
            <div className="bg-white rounded-lg p-4">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Origen
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Reservas
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Comensales
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Confirmadas
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Tasa Éxito
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {Object.entries(marketingMetrics.bySource).map(([source, data]) => (
                      <tr key={source} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                            {source}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {data.count}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {data.guests}
                        </td>
                        <td className="px-4 py-3 text-sm text-green-600 font-medium">
                          {data.confirmed}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-green-500 h-2 rounded-full"
                                style={{ width: `${data.count > 0 ? (data.confirmed / data.count) * 100 : 0}%` }}
                              ></div>
                            </div>
                            <span className="text-gray-900 font-medium">
                              {data.count > 0 ? ((data.confirmed / data.count) * 100).toFixed(0) : 0}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Mejor origen */}
              {marketingMetrics.topPerformers.bestSource && (
                <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-900">
                    🏆 Mejor origen: <strong>{marketingMetrics.topPerformers.bestSource.source}</strong> con {marketingMetrics.topPerformers.bestSource.data.count} reservas
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Vista Combinada */}
          {marketingView === 'combo' && Object.keys(marketingMetrics.byCombo).length > 0 && (
            <div className="bg-white rounded-lg p-4">
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Promoción
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Origen
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Reservas
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Comensales
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-700 uppercase">
                        Confirmadas
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {Object.values(marketingMetrics.byCombo)
                      .sort((a, b) => b.count - a.count)
                      .slice(0, 10) // Top 10 combinaciones
                      .map((combo) => (
                      <tr key={`${combo.promo}_${combo.source}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">
                            {combo.promo}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                            {combo.source}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {combo.count}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {combo.guests}
                        </td>
                        <td className="px-4 py-3 text-sm text-green-600 font-medium">
                          {combo.confirmed}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Mejor combinación */}
              {marketingMetrics.topPerformers.bestCombo && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-900">
                    🏆 Mejor combinación: <strong>{marketingMetrics.topPerformers.bestCombo.promo}</strong> + <strong>{marketingMetrics.topPerformers.bestCombo.source}</strong> con {marketingMetrics.topPerformers.bestCombo.count} reservas
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tarjetas de métricas principales */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total de Reservas */}
          <div 
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => navigate('/admin/reservas')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Reservas</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">
                  {stats.overview.totalReservations}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {stats.overview.averageReservationsPerDay} promedio/día
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Total de Comensales */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Comensales</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">
                  {stats.overview.totalGuests}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {stats.overview.averageGuestsPerDay} promedio/día
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* Tamaño Promedio de Grupo */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Tamaño Promedio Grupo</p>
                <h3 className="text-3xl font-bold text-gray-900 mt-2">
                  {stats.overview.averagePartySize}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  personas por reserva
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          {/* Nivel de Ocupación */}
          {occupancyData && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Nivel de Ocupación</p>
                  <h3 className="text-3xl font-bold text-gray-900 mt-2">
                    {occupancyData.occupancyPercentage}%
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {occupancyData.averageDailyOccupancy}% promedio/día
                  </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-full">
                  <PieChart className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              {/* Barra de progreso */}
              <div className="mt-4">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Capacidad utilizada</span>
                  <span>{occupancyData.totalGuests} / {occupancyData.maxPeriodCapacity}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-orange-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, occupancyData.occupancyPercentage)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Métricas por horario de comida */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Comidas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Comidas (13:00-17:00)</h3>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-gray-600">Reservas</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {stats.byMealTime.lunch.totalReservations}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.byMealTime.lunch.averagePerDay} promedio/día
                </p>
              </div>
              
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-gray-600">Comensales</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {stats.byMealTime.lunch.totalGuests}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.byMealTime.lunch.averagePartySize} promedio/grupo
                </p>
              </div>
            </div>
            
            {/* Porcentaje del total */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Porcentaje del total</span>
                <span className="font-medium">
                  {Math.round((stats.byMealTime.lunch.totalReservations / stats.overview.totalReservations) * 100)}% reservas
                </span>
              </div>
            </div>
          </div>

          {/* Cenas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Cenas (17:00-23:00)</h3>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Clock className="h-5 w-5 text-indigo-600" />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <p className="text-sm font-medium text-gray-600">Reservas</p>
                <p className="text-2xl font-bold text-indigo-600 mt-1">
                  {stats.byMealTime.dinner.totalReservations}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.byMealTime.dinner.averagePerDay} promedio/día
                </p>
              </div>
              
              <div className="text-center p-4 bg-indigo-50 rounded-lg">
                <p className="text-sm font-medium text-gray-600">Comensales</p>
                <p className="text-2xl font-bold text-indigo-600 mt-1">
                  {stats.byMealTime.dinner.totalGuests}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.byMealTime.dinner.averagePartySize} promedio/grupo
                </p>
              </div>
            </div>
            
            {/* Porcentaje del total */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Porcentaje del total</span>
                <span className="font-medium">
                  {Math.round((stats.byMealTime.dinner.totalReservations / stats.overview.totalReservations) * 100)}% reservas
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Gráfico de tendencia diaria */}
      {chartData && chartData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Tendencia de Reservas y Comensales</h3>
            <div className="p-2 bg-green-100 rounded-lg">
              <BarChart3 className="h-5 w-5 text-green-600" />
            </div>
          </div>
          
          {/* Mini gráfico simple usando CSS */}
          <div className="space-y-4">
            {/* Leyenda */}
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span>Reservas</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span>Comensales</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-orange-500 rounded"></div>
                <span>Comidas</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-500 rounded"></div>
                <span>Cenas</span>
              </div>
            </div>
            
            {/* Datos tabulares en vista de gráfico */}
            <div className="overflow-x-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Tabla de reservas por día */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Reservas por día</h4>
                  <div className="space-y-2">
                    {chartData.map((day, index) => {
                      const maxReservations = Math.max(...chartData.map(d => d.totalReservations));
                      const percentage = maxReservations > 0 ? (day.totalReservations / maxReservations) * 100 : 0;
                      
                      return (
                        <div key={index} className="flex items-center gap-3">
                          <span className="text-xs font-medium text-gray-600 w-12">
                            {day.date}
                          </span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2 relative">
                            <div 
                              className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-xs font-medium text-gray-900 w-8 text-right">
                            {day.totalReservations}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Tabla de comensales por día */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Comensales por día</h4>
                  <div className="space-y-2">
                    {chartData.map((day, index) => {
                      const maxGuests = Math.max(...chartData.map(d => d.totalGuests));
                      const percentage = maxGuests > 0 ? (day.totalGuests / maxGuests) * 100 : 0;
                      
                      return (
                        <div key={index} className="flex items-center gap-3">
                          <span className="text-xs font-medium text-gray-600 w-12">
                            {day.date}
                          </span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2 relative">
                            <div 
                              className="bg-green-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-xs font-medium text-gray-900 w-8 text-right">
                            {day.totalGuests}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Resumen del período */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-600">Día con más reservas</p>
                  <p className="font-semibold text-blue-600">
                    {chartData.reduce((max, day) => day.totalReservations > max.totalReservations ? day : max, chartData[0])?.date}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Día con más comensales</p>
                  <p className="font-semibold text-green-600">
                    {chartData.reduce((max, day) => day.totalGuests > max.totalGuests ? day : max, chartData[0])?.date}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total de comidas</p>
                  <p className="font-semibold text-orange-600">
                    {chartData.reduce((sum, day) => sum + day.lunchReservations, 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total de cenas</p>
                  <p className="font-semibold text-purple-600">
                    {chartData.reduce((sum, day) => sum + day.dinnerReservations, 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Estadísticas por estado y origen */}
      {stats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Estados de las reservas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Estados de las Reservas</h3>
            
            <div className="space-y-3">
              {[
                { key: 'confirmed', label: 'Confirmadas', value: stats.byStatus.confirmed, color: 'bg-green-500' },
                { key: 'pending', label: 'Pendientes', value: stats.byStatus.pending, color: 'bg-yellow-500' },
                { key: 'seated', label: 'Sentadas', value: stats.byStatus.seated, color: 'bg-purple-500' },
                { key: 'completed', label: 'Completadas', value: stats.byStatus.completed, color: 'bg-blue-500' },
                { key: 'cancelled', label: 'Canceladas', value: stats.byStatus.cancelled, color: 'bg-red-500' },
                { key: 'noShow', label: 'No Show', value: stats.byStatus.noShow, color: 'bg-gray-500' }
              ].map(status => {
                const total = Object.values(stats.byStatus).reduce((sum, val) => sum + val, 0);
                const percentage = total > 0 ? Math.round((status.value / total) * 100) : 0;
                
                return (
                  <div key={status.key} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${status.color}`}></div>
                      <span className="text-sm font-medium text-gray-700">{status.label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${status.color} transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-bold text-gray-900 w-8 text-right">
                        {status.value}
                      </span>
                      <span className="text-xs text-gray-500 w-10 text-right">
                        ({percentage}%)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Origen de las reservas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Origen de las Reservas</h3>
            
            <div className="space-y-3">
              {[
                { key: 'web', label: 'Web', value: stats.bySource.web, color: 'bg-blue-500' },
                { key: 'google', label: 'Google', value: stats.bySource.google, color: 'bg-green-500' },
                { key: 'walkIn', label: 'Walk-in', value: stats.bySource.walkIn, color: 'bg-purple-500' },
                { key: 'admin', label: 'Admin', value: stats.bySource.admin, color: 'bg-gray-500' },
                { key: 'special', label: 'Especiales', value: stats.bySource.special, color: 'bg-pink-500' }
              ].map(source => {
                const total = Object.values(stats.bySource).reduce((sum, val) => sum + val, 0);
                const percentage = total > 0 ? Math.round((source.value / total) * 100) : 0;
                
                return (
                  <div key={source.key} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${source.color}`}></div>
                      <span className="text-sm font-medium text-gray-700">{source.label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${source.color} transition-all duration-500`}
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-bold text-gray-900 w-8 text-right">
                        {source.value}
                      </span>
                      <span className="text-xs text-gray-500 w-10 text-right">
                        ({percentage}%)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Información adicional de ocupación */}
      {occupancyData && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Detalles de Ocupación</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-sm font-medium text-gray-600">Capacidad Total por Mesa</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {occupancyData.totalCapacity}
              </p>
              <p className="text-xs text-gray-500 mt-1">personas por turno</p>
            </div>
            
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-sm font-medium text-gray-600">Capacidad Diaria Máxima</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {occupancyData.maxDailyCapacity}
              </p>
              <p className="text-xs text-gray-500 mt-1">personas/día (2 turnos)</p>
            </div>
            
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-sm font-medium text-gray-600">Capacidad del Período</p>
              <p className="text-2xl font-bold text-orange-600 mt-1">
                {occupancyData.maxPeriodCapacity}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                total en {stats?.overview.totalDays} días
              </p>
            </div>
          </div>
          
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Análisis de Ocupación</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Utilización actual:</span>
                <span className="ml-2 font-semibold">
                  {occupancyData.occupancyPercentage}% del período
                </span>
              </div>
              <div>
                <span className="text-gray-600">Promedio diario:</span>
                <span className="ml-2 font-semibold">
                  {occupancyData.averageDailyOccupancy}% por día
                </span>
              </div>
            </div>
            
            <div className="mt-3 text-xs text-gray-500">
              * Cálculo basado en 2 turnos por día (comida y cena) y la capacidad total de mesas
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;